// Workstream: growth. Analytics, retention and event-kit enquiries.
import { useCallback, useEffect, useState } from 'react';
import { usePrefs } from '../../components/Prefs';
import { EVENT_TYPE_LABELS, growthApi, type Enquiry, type EventType, type GrowthSummary } from '../marketing/api';

type Day = GrowthSummary['daily'][number];
interface Series {
  key: 'devices' | 'started' | 'finished';
  label: string;
  /** CSS class that sets the fill colour (validated for colour-blind separation in both themes). */
  className: string;
}

const pct = (n: number, of: number) => (of > 0 ? `${Math.round((n / of) * 100)}%` : '–');
const shortDay = (day: string) => new Date(`${day}T12:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

function Stat({ n, l, sub }: { n: number | string; l: string; sub?: string }) {
  return (
    <div className="stat">
      <div className="n">{n}</div>
      <div className="l">{l}</div>
      {sub && <div className="l gr-sub">{sub}</div>}
    </div>
  );
}

/** Centre the tooltip on its bar, but pin it to the plot's edge near either end so it never spills out. */
function tipPosition(i: number, n: number): React.CSSProperties {
  if (i < 4) return { left: `${(i / n) * 100}%` };
  if (i > n - 5) return { left: `${((i + 1) / n) * 100}%`, transform: 'translateX(-100%)' };
  return { left: `${((i + 0.5) / n) * 100}%`, transform: 'translateX(-50%)' };
}

/** Daily bars with a hover/focus tooltip, a legend for two or more series, and a table view. */
function DailyChart({ title, days, series }: { title: string; days: Day[]; series: Series[] }) {
  const [active, setActive] = useState<number | null>(null);
  const max = Math.max(1, ...days.flatMap((d) => series.map((s) => d[s.key])));
  const focus = active === null ? null : days[active];
  return (
    <div className="panel gr-chart">
      <div className="row between">
        <h3 style={{ margin: 0 }}>{title}</h3>
        {series.length > 1 && (
          <div className="gr-legend">
            {series.map((s) => (
              <span key={s.key}>
                <i className={s.className} aria-hidden="true" />
                {s.label}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="gr-plot" onMouseLeave={() => setActive(null)}>
        <span className="gr-max">{max}</span>
        <div className="gr-bars" role="list" aria-label={title}>
          {days.map((d, i) => (
            <div
              key={d.day}
              role="listitem"
              tabIndex={0}
              className={`gr-group ${active === i ? 'on' : ''}`}
              aria-label={`${shortDay(d.day)}: ${series.map((s) => `${d[s.key]} ${s.label.toLowerCase()}`).join(', ')}`}
              onMouseEnter={() => setActive(i)}
              onFocus={() => setActive(i)}
              onBlur={() => setActive(null)}
            >
              {series.map((s) => (
                <span key={s.key} className={`gr-bar ${s.className}`} style={{ height: `${(d[s.key] / max) * 100}%` }} />
              ))}
            </div>
          ))}
        </div>
        {focus && (
          <div className="gr-tip" style={tipPosition(active!, days.length)} role="status">
            <strong>{shortDay(focus.day)}</strong>
            {series.map((s) => (
              <span key={s.key}>
                <i className={s.className} aria-hidden="true" />
                {s.label}: <b>{focus[s.key]}</b>
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="gr-axis muted small">
        <span>{days[0] && shortDay(days[0].day)}</span>
        <span>{days.length > 0 && shortDay(days[days.length - 1].day)}</span>
      </div>
      <details className="small">
        <summary>Show as a table</summary>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Day</th>
                {series.map((s) => (
                  <th key={s.key}>{s.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...days].reverse().map((d) => (
                <tr key={d.day}>
                  <td>{d.day}</td>
                  {series.map((s) => (
                    <td key={s.key}>{d[s.key]}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}

function Funnel({ f }: { f: GrowthSummary['funnel'] }) {
  const steps = [
    { label: 'Visited the site', n: f.visited },
    { label: 'Created or joined a room', n: f.inRoom },
    { label: 'Started a game', n: f.started },
    { label: 'Finished a game', n: f.finished },
  ];
  const top = Math.max(1, f.visited, f.inRoom);
  return (
    <div className="panel">
      <h3>Funnel, last 30 days</h3>
      <p className="muted small">Unique devices at each step. The percentage is of the step before.</p>
      <ol className="gr-funnel">
        {steps.map((s, i) => (
          <li key={s.label}>
            <div className="row between small">
              <span>{s.label}</span>
              <span>
                <strong>{s.n}</strong> {i > 0 && <span className="muted">({pct(s.n, steps[i - 1].n)})</span>}
              </span>
            </div>
            <div className="gr-track">
              <span style={{ width: `${(s.n / top) * 100}%` }} />
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function RankTable({ title, rows, empty }: { title: string; rows: { label: string; n: number }[]; empty: string }) {
  return (
    <div className="panel">
      <h3>{title}</h3>
      {rows.length ? (
        <table className="table gr-rank">
          <tbody>
            {rows.map((r) => (
              <tr key={r.label}>
                <td className="gr-label">{r.label}</td>
                <td className="gr-n">{r.n}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="muted small">{empty}</p>
      )}
    </div>
  );
}

function Enquiries({ items, onToggle }: { items: Enquiry[]; onToggle: (e: Enquiry) => void }) {
  if (!items.length) return <p className="muted">No event enquiries yet. They arrive from the form on /events.</p>;
  return (
    <div className="stack">
      {items.map((e) => (
        <article key={e.id} className={`suggestion gr-enquiry ${e.handled ? 'handled' : ''}`}>
          <div className="row between">
            <div>
              <strong>{e.name}</strong> <span className="chip blue">{EVENT_TYPE_LABELS[e.kind as EventType] ?? e.kind}</span>
              <div className="small">
                <a href={`mailto:${e.email}`}>{e.email}</a>
                {e.org && <span className="muted"> · {e.org}</span>}
              </div>
            </div>
            <label className="check small">
              <input type="checkbox" checked={e.handled} onChange={() => onToggle(e)} />
              Handled
            </label>
          </div>
          <div className="small muted">
            {[e.eventDate && `When: ${e.eventDate}`, e.guests && `Guests: ${e.guests}`, `Sent ${new Date(e.createdAt).toLocaleString()}`].filter(Boolean).join(' · ')}
          </div>
          {e.message && <p className="gr-message">{e.message}</p>}
        </article>
      ))}
    </div>
  );
}

export function GrowthTab() {
  const [s, setS] = useState<GrowthSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const { toast } = usePrefs();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setS(await growthApi.summary());
    } catch (e) {
      toast((e as Error).message, 'error');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  async function toggle(e: Enquiry) {
    const handled = !e.handled;
    setS((cur) => cur && { ...cur, enquiries: cur.enquiries.map((x) => (x.id === e.id ? { ...x, handled } : x)) });
    try {
      await growthApi.setHandled(e.id, handled);
    } catch (err) {
      toast((err as Error).message, 'error');
      void load();
    }
  }

  if (!s) return <p className="muted">{loading ? 'Loading...' : 'Could not load the numbers.'}</p>;
  const { totals, retention } = s;
  const open = s.enquiries.filter((e) => !e.handled).length;

  return (
    <section className="stack gr">
      <div className="row between">
        <p className="muted small" style={{ margin: 0 }}>
          First-party and cookie-free. A device is one browser; bots and link previews are not counted.
        </p>
        <button className="btn btn-sm btn-outline" onClick={() => void load()} disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      <div className="stat-grid">
        <Stat n={totals.devices7} l="Active devices, 7 days" />
        <Stat n={totals.devices30} l="Active devices, 30 days" />
        <Stat n={totals.pageViews30} l="Page views, 30 days" />
        <Stat n={totals.games30} l="Games started, 30 days" sub={`${pct(totals.finished30, totals.games30)} finished`} />
        <Stat n={open} l="Open event enquiries" />
      </div>

      <DailyChart title="Daily active devices" days={s.daily} series={[{ key: 'devices', label: 'Devices', className: 'gr-c1' }]} />
      <DailyChart
        title="Games per day"
        days={s.daily}
        series={[
          { key: 'started', label: 'Started', className: 'gr-c2' },
          { key: 'finished', label: 'Finished', className: 'gr-c1' },
        ]}
      />

      <div className="grid-2">
        <Funnel f={s.funnel} />
        <div className="panel">
          <h3>Coming back</h3>
          <p className="muted small">Games started in the last 30 days.</p>
          <div className="stat-grid">
            <Stat
              n={pct(retention.devicesMultiDay, retention.devicesPlayed)}
              l="Played on 2 or more days"
              sub={`${retention.devicesMultiDay} of ${retention.devicesPlayed} devices`}
            />
            <Stat
              n={pct(retention.hostsReturned, retention.hostedRooms)}
              l="Hosts who played again within 14 days"
              sub={`${retention.hostsReturned} of ${retention.hostedRooms} rooms`}
            />
          </div>
          <p className="muted small" style={{ marginTop: 10, marginBottom: 0 }}>
            "Played again" means another game at least 6 hours later. Rooms from the last two weeks are still counting.
          </p>
        </div>
      </div>

      <div className="grid-2">
        <RankTable
          title="Landing pages"
          rows={s.landingPages.map((r) => ({ label: r.path, n: r.views }))}
          empty="No visits yet."
        />
        <RankTable title="Where visitors came from" rows={s.referrers.map((r) => ({ label: r.source, n: r.views }))} empty="No outside referrers yet." />
      </div>
      <RankTable title="Button and share clicks, 30 days" rows={s.ctas.map((r) => ({ label: r.cta, n: r.clicks }))} empty="No clicks recorded yet." />

      <div className="panel">
        <h3>Client errors, last 14 days</h3>
        {s.errors.length ? (
          <div className="stack" style={{ gap: 8 }}>
            {s.errors.map((e) => (
              <details key={e.message} className="gr-error">
                <summary>
                  <span className="chip red">{e.count}x</span> <span className="gr-error-msg">{e.message}</span>
                  <span className="muted small">
                    {' '}
                    · {e.path ?? 'unknown page'} · {new Date(e.lastSeen).toLocaleString()}
                  </span>
                </summary>
                <pre>{e.stack || 'No stack trace.'}</pre>
              </details>
            ))}
          </div>
        ) : (
          <p className="muted small">No errors reported. Shabash.</p>
        )}
      </div>

      <div>
        <h2>Event enquiries</h2>
        <Enquiries items={s.enquiries} onToggle={toggle} />
      </div>
    </section>
  );
}
